import type { MutationUpdaterFn } from '@apollo/client';
import { gql, useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import useNavigate from '../utils/router/useNavigate';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import type {
  AllTopicsWithTemplatesQuery,
  AllTopicsWithTemplatesQueryVariables,
  TemplatesModalPersonalTemplatesQuery,
  TemplatesModalPersonalTemplatesQueryVariables,
  TemplatesModalTemplateContentQuery,
  TemplatesModalTemplateContentQueryVariables,
  TemplatesModalTemplatePostContentQuery,
  TemplatesModalTemplatePostContentQueryVariables,
  TemplatesModalTemplatesQuery,
  TemplatesModalTemplatesQueryVariables,
  TemplatesModalTopicFragment,
  UpdateTemplateMutation,
  UpdateTemplateMutationVariables,
  UpdateTemplateOwnerMutation,
  UpdateTemplateOwnerMutationVariables,
} from '../graphql/generated/graphqlTypes';
import closeIcon from '../icons/clear.svg';
import topicPolicyCheck from '../topic/topicPolicyCheck';
import type { TreeListTopic } from '../topic/toTopicTreeList';
import toTopicTreeList, {
  flattenTopicTreeList,
} from '../topic/toTopicTreeList';
import useMembershipTopics from '../topic/useMembershipTopics';
import UIActions from '../ui/UIActions';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useErrorRedirectEffect from '../utils/error/useErrorRedirectEffect';
import updateApolloList from '../utils/graphql/updateApolloList';
import updateQueryList from '../utils/graphql/updateQueryList';
import useSlug from '../utils/router/useSlug';
import templatePath, { getPreviousRoute } from '../utils/url/templatesPath';
import EmptyTemplate from './EmptyTemplate';
import TemplateContent from './TemplateContent';
import TemplateMobileHeader from './TemplateMobileHeader';
import styles from './TemplatesModal.module.scss';
import TemplatesSidebar from './TemplatesSidebar';
import useDeleteTemplateMutation from './useDeleteTemplateMutation';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import { isFeatureEnabled } from '../utils/features';
import useDeletePost from '../post/useDeletePost';

const TEMPLATE_CONTENT_QUERY = gql`
  query TemplatesModalTemplateContent($id: ID!) {
    template(id: $id) {
      id
      topicOwner {
        id
        ...TemplateContent_topic
      }
      userOwner {
        id
        ...TemplateContent_owner
      }
      ...TemplateContent_template
    }
  }
  ${
    // @ts-expect-error
    TemplateContent.fragments.template
  }
  ${
    // @ts-expect-error
    TemplateContent.fragments.topic
  }
  ${
    // @ts-expect-error
    TemplateContent.fragments.owner
  }
`;

const TEMPLATES_TEMPLATE_FRAGMENT = gql`
  fragment TemplatesModalTemplate on Template {
    id
    ...TemplatesSidebar_templates
  }
  ${TemplatesSidebar.fragments.templates}
`;

const TEMPLATES_MODAL_TEMPLATE_POST_FRAGMENT = gql`
  fragment TemplatesModalTemplatePost on Post {
    id
    ...TemplatesSidebar_templatePosts
  }
  ${TemplatesSidebar.fragments.templatePosts}
`;

const TEMPLATES_TOPIC_FRAGMENT = gql`
  fragment TemplatesModalTopic on Topic {
    id
    name
    templates {
      id
      ...TemplatesModalTemplate
    }
    allPosts(orderBy: UPDATED_AT, type: TEMPLATE) {
      edges {
        node {
          id
          ...TemplatesModalTemplatePost
        }
      }
    }
    inheritParent
    parent {
      id
      name
    }
    ancestors {
      id
      name
    }
    ...toTopicTreeList_topic
    ...TemplatesSidebar_topic
  }

  ${TEMPLATES_TEMPLATE_FRAGMENT}
  ${TEMPLATES_MODAL_TEMPLATE_POST_FRAGMENT}
  ${toTopicTreeList.fragments.topic}
  ${TemplatesSidebar.fragments.topic}
`;

const ALL_TOPICS_WITH_TEMPLATES_QUERY = gql`
  query allTopicsWithTemplates {
    session: currentSession {
      organization {
        id
        topics {
          id
          ...TemplatesModalTopic
        }
      }
    }
  }

  ${TEMPLATES_TOPIC_FRAGMENT}
`;

const TEMPLATES_QUERY = gql`
  query TemplatesModalTemplates {
    session: currentSession {
      user {
        id
        templates {
          id
          ...TemplatesModalTemplate
        }
        membershipTopics(includeGroups: true) {
          id
          ...useMembershipTopics_topic
        }
      }
    }
  }

  ${TEMPLATES_TEMPLATE_FRAGMENT}
  ${useMembershipTopics.fragments.topic}
`;

export const getTemplateQueryUpdate =
  (dataKey = 'template', add = true): MutationUpdaterFn =>
  (proxy, { data }) =>
    updateQueryList(
      { query: TEMPLATES_QUERY },
      'session.user.templates',
      dataKey,
      add,
    )(proxy, { data });

export const getTemplateOwnersQueryUpdate =
  (
    oldOwnerId: string | null,
    newOwnerId: string | null,
    dataKey: string,
  ): MutationUpdaterFn =>
  (proxy, { data }) => {
    if (!data) return;
    const prevTopics = proxy.readQuery<
      AllTopicsWithTemplatesQuery,
      AllTopicsWithTemplatesQueryVariables
    >({ query: ALL_TOPICS_WITH_TEMPLATES_QUERY });

    const oldTopicOwnerIndex = oldOwnerId
      ? prevTopics?.session?.organization?.topics?.findIndex(
          (topic) => topic?.id === oldOwnerId,
        ) ?? -1
      : -1;

    const updatedOldTopic =
      prevTopics && oldTopicOwnerIndex >= 0
        ? updateApolloList(
            prevTopics,
            data,
            `session.organization.topics[${oldTopicOwnerIndex}].templates`,
            dataKey,
            false,
          )
        : prevTopics;

    const newTopicOwnerIndex = newOwnerId
      ? prevTopics?.session?.organization?.topics?.findIndex(
          (topic) => topic?.id === newOwnerId,
        ) ?? -1
      : -1;

    const updatedNewTopic =
      updatedOldTopic && newTopicOwnerIndex >= 0
        ? updateApolloList(
            updatedOldTopic,
            data,
            `session.organization.topics[${newTopicOwnerIndex}].templates`,
            'updateTemplateOwner',
            true,
          )
        : updatedOldTopic;

    if (updatedNewTopic) {
      proxy.writeQuery<
        AllTopicsWithTemplatesQuery,
        AllTopicsWithTemplatesQueryVariables
      >({
        query: ALL_TOPICS_WITH_TEMPLATES_QUERY,
        data: { ...updatedNewTopic },
      });
    }
  };

const UPDATE_TEMPLATE_CONTENT = gql`
  mutation updateTemplate($id: ID!, $content: Json!) {
    template: updateTemplate(id: $id, content: $content) {
      id
      title
      content
    }
  }
`;

const UPDATE_TEMPLATE_OWNER = gql`
  mutation UpdateTemplateOwner($id: ID!, $topicId: ID) {
    updateTemplateOwner(id: $id, topicId: $topicId) {
      id
      ...TemplatesModalTemplate
      topicOwner {
        id
        name
        ...topicPolicyCheck_topic
      }
      userOwner {
        id
        name
      }
    }
  }
  ${
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
    topicPolicyCheck.fragments.topic
  }
  ${TEMPLATES_TEMPLATE_FRAGMENT}
`;

const filterTreeTopicsWithoutTemplate = (
  topics?: TreeListTopic<TemplatesModalTopicFragment>[],
): TreeListTopic<TemplatesModalTopicFragment>[] => {
  if (!topics || topics.length === 0) {
    return [];
  }
  return topics
    .map((topic) => ({
      ...topic,
      childTopics: filterTreeTopicsWithoutTemplate(topic.childTopics),
    }))
    .filter(
      (topic) =>
        topic.childTopics.length > 0 || (topic.templates?.length ?? 0) > 0,
    );
};

const filterTreeTopicsWithoutTemplatePosts = (
  topics?: TreeListTopic<TemplatesModalTopicFragment>[],
): TreeListTopic<TemplatesModalTopicFragment>[] => {
  if (!topics || topics.length === 0) {
    return [];
  }
  return topics
    .map((topic) => ({
      ...topic,
      childTopics: filterTreeTopicsWithoutTemplatePosts(topic.childTopics),
    }))
    .filter(
      (topic) =>
        topic.childTopics.length > 0 ||
        (topic.allPosts?.edges?.length ?? 0) > 0,
    );
};

const TEMPLATE_POST_CONTENT_QUERY = gql`
  query TemplatesModalTemplatePostContent($id: ID!) {
    post(id: $id) {
      id
      title
      content
      topics {
        id
        ...TemplateContent_topic
      }
      owner {
        id
        ...TemplateContent_owner
      }
    }
  }
  ${
    // @ts-expect-error
    TemplateContent.fragments.topic
  }
  ${
    // @ts-expect-error
    TemplateContent.fragments.owner
  }
`;

const TEMPLATE_PERSONAL_TEMPLATES_QUERY = gql`
  query TemplatesModalPersonalTemplates($afterCursor: String) {
    session: currentSession {
      user {
        id
        posts(first: 100, filter: TEMPLATE, after: $afterCursor) {
          edges {
            node {
              id
              ...TemplatesSidebar_templatePosts
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        membershipTopics(includeGroups: true) {
          id
          ...useMembershipTopics_topic
        }
      }
    }
  }
  ${TemplatesSidebar.fragments.templatePosts}
  ${useMembershipTopics.fragments.topic}
`;

interface TemplatesPostModalProps {
  activeTemplateId: string | null;
  onClose?: () => void;
  onActiveTemplateIdChange?: (id: string | null) => void;
}

const TemplatesPostModal = ({
  activeTemplateId,
  onClose,
  onActiveTemplateIdChange,
}: TemplatesPostModalProps) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(
    !activeTemplateId,
  );

  const { data, loading } = useQuery<
    TemplatesModalTemplatePostContentQuery,
    TemplatesModalTemplatePostContentQueryVariables
  >(TEMPLATE_POST_CONTENT_QUERY, {
    skip: !activeTemplateId,
    variables: { id: activeTemplateId ?? '' },
  });

  const {
    data: allTopicsWithTemplatePosts,
    refetch: refetchAllTopicsWithTemplatePosts,
  } = useQuery<
    AllTopicsWithTemplatesQuery,
    AllTopicsWithTemplatesQueryVariables
  >(ALL_TOPICS_WITH_TEMPLATES_QUERY);

  const personalTemplatesResult = useQuery<
    TemplatesModalPersonalTemplatesQuery,
    TemplatesModalPersonalTemplatesQueryVariables
  >(TEMPLATE_PERSONAL_TEMPLATES_QUERY);

  const fetchMore = ConnectionHelper.generateFetchMore(
    TEMPLATE_PERSONAL_TEMPLATES_QUERY,
    personalTemplatesResult,
    'session.user.posts',
  );

  const refetchPersonalTemplates = personalTemplatesResult.refetch;

  const templatePost = data?.post ?? null;
  const userTemplates = _.compact(
    personalTemplatesResult.data?.session.user.posts?.edges?.map(
      (e) => e?.node,
    ),
  );

  const allTopics = useMemo(() => {
    const list = toTopicTreeList<TemplatesModalTopicFragment>(
      allTopicsWithTemplatePosts?.session?.organization?.topics ?? [],
    );
    return flattenTopicTreeList(list);
  }, [allTopicsWithTemplatePosts]);

  const membershipTopics = useMembershipTopics(
    allTopics,
    personalTemplatesResult.data?.session?.user?.membershipTopics,
  );

  const topicsTree = useMemo(
    () => filterTreeTopicsWithoutTemplatePosts(membershipTopics ?? []),
    [membershipTopics],
  );

  const navigate = useNavigate();
  const deletePost = useDeletePost();

  return (
    <div className={styles.container}>
      <div className={styles.modal}>
        <ClickOutsideHandler
          className={styles.sidebarContainer}
          onClickOutside={() => setIsMobileSidebarOpen(false)}
        >
          <TemplatesSidebar
            topicsTree={topicsTree}
            userTemplates={userTemplates}
            activeTemplateId={activeTemplateId}
            mobileOpen={isMobileSidebarOpen}
            loading={personalTemplatesResult.loading}
            onClickTemplate={onActiveTemplateIdChange}
            fetchMore={fetchMore}
          />
        </ClickOutsideHandler>
        <div
          className={classNames(styles.contentContainer, {
            [styles.empty]: !activeTemplateId,
          })}
        >
          <TemplateMobileHeader
            openSidebar={() => setIsMobileSidebarOpen(true)}
            onClose={onClose}
          />
          {activeTemplateId ? (
            <TemplateContent
              template={templatePost}
              loading={loading}
              owner={templatePost?.owner ?? null}
              topics={templatePost?.topics ?? []}
              deleteTemplate={async () => {
                if (!templatePost) return;
                await deletePost(templatePost.id);
                refetchAllTopicsWithTemplatePosts();
                refetchPersonalTemplates();
                if (onActiveTemplateIdChange) {
                  onActiveTemplateIdChange(null);
                } else {
                  navigate(templatePath());
                }
              }}
            />
          ) : (
            <EmptyTemplate
              hasTemplates={userTemplates.length > 0 || topicsTree.length > 0}
            />
          )}
        </div>
      </div>
      <Button
        className={styles.desktopClose}
        onClick={onClose}
        to={onClose ? undefined : getPreviousRoute()}
      >
        <SVGIcon size="small">{closeIcon}</SVGIcon>
      </Button>
    </div>
  );
};

interface BaseTemplatesModalProps {
  activeTemplateId: string | null;
  onDeleteTemplate: () => void;
  onActiveTemplateIdChange?: (id: string | null) => void;
  onClose?: () => void;
  onCreatePost?: () => void;
}

const BaseTemplatesModal = ({
  activeTemplateId,
  onDeleteTemplate,
  onActiveTemplateIdChange,
  onCreatePost,
  onClose,
}: BaseTemplatesModalProps) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(
    !activeTemplateId,
  );

  const dispatch = useDispatch();

  const {
    data: { template = null } = {},
    error,
    loading,
  } = useQuery<
    TemplatesModalTemplateContentQuery,
    TemplatesModalTemplateContentQueryVariables
  >(TEMPLATE_CONTENT_QUERY, {
    skip: !activeTemplateId,
    variables: { id: activeTemplateId ?? '' },
  });

  const { data: templatesQueryData, loading: isTemplatesQueryLoading } =
    useQuery<
      TemplatesModalTemplatesQuery,
      TemplatesModalTemplatesQueryVariables
    >(TEMPLATES_QUERY);

  const { data: allTopicsWithTemplates } = useQuery<
    AllTopicsWithTemplatesQuery,
    AllTopicsWithTemplatesQueryVariables
  >(ALL_TOPICS_WITH_TEMPLATES_QUERY);

  const [updateTemplateContent] = useMutation<
    UpdateTemplateMutation,
    UpdateTemplateMutationVariables
  >(UPDATE_TEMPLATE_CONTENT);

  const [updateTemplateOwner] = useMutation<
    UpdateTemplateOwnerMutation,
    UpdateTemplateOwnerMutationVariables
  >(UPDATE_TEMPLATE_OWNER, {
    update: (cache, { data: updatedOwnerData }) => {
      const topicOwner = updatedOwnerData?.updateTemplateOwner?.topicOwner;
      const userOwner = updatedOwnerData?.updateTemplateOwner?.userOwner;
      const templateId = updatedOwnerData?.updateTemplateOwner?.id;

      if (templateId) {
        const prevTemplate = cache.readQuery<
          TemplatesModalTemplateContentQuery,
          TemplatesModalTemplateContentQueryVariables
        >({
          query: TEMPLATE_CONTENT_QUERY,
          variables: { id: templateId },
        });

        if (prevTemplate?.template) {
          cache.writeQuery<
            TemplatesModalTemplateContentQuery,
            TemplatesModalTemplateContentQueryVariables
          >({
            query: TEMPLATE_CONTENT_QUERY,
            variables: { id: prevTemplate.template.id },
            data: {
              template: {
                ...prevTemplate.template,
                ...updatedOwnerData?.updateTemplateOwner,
              },
            },
          });
        }
      }

      getTemplateQueryUpdate('updateTemplateOwner', Boolean(userOwner))(cache, {
        data: updatedOwnerData,
      });

      getTemplateOwnersQueryUpdate(
        template?.topicOwner?.id ?? null,
        topicOwner?.id ?? null,
        'updateTemplateOwner',
      )(cache, { data: updatedOwnerData });
    },
  });

  const [deleteTemplate] = useDeleteTemplateMutation();

  const allTopics = useMemo(() => {
    const list = toTopicTreeList<TemplatesModalTopicFragment>(
      allTopicsWithTemplates?.session?.organization?.topics ?? [],
    );
    return flattenTopicTreeList(list);
  }, [allTopicsWithTemplates]);

  const membershipTopics = useMembershipTopics(
    allTopics,
    templatesQueryData?.session?.user?.membershipTopics,
  );

  const topicsTree = useMemo(
    () => filterTreeTopicsWithoutTemplate(membershipTopics ?? []),
    [membershipTopics],
  );

  const userTemplates = templatesQueryData?.session?.user?.templates ?? [];

  useErrorRedirectEffect({
    noun: 'template',
    error,
    redirectPath: '/templates',
  });

  useEffect(() => {
    if (activeTemplateId) {
      setIsMobileSidebarOpen(false);
    }
  }, [activeTemplateId]);

  const handleUpdateTemplateContent = (content: string) => {
    if (!template) {
      return null;
    }
    return updateTemplateContent({ variables: { id: template.id, content } });
  };

  const handleUpdateTemplateOwner = (topicId?: string) => {
    if (!template) {
      return null;
    }
    return updateTemplateOwner({ variables: { id: template.id, topicId } });
  };

  const handleDeleteTemplate = async () => {
    if (!template) {
      return;
    }
    await deleteTemplate({ variables: { id: template.id } });
    dispatch(
      UIActions.pushToast({
        level: 'success',
        message: 'Template deleted',
        hideIcon: true,
      }),
    );

    onDeleteTemplate();
  };

  return (
    <div className={styles.container}>
      <div className={styles.modal}>
        <ClickOutsideHandler
          className={styles.sidebarContainer}
          onClickOutside={() => setIsMobileSidebarOpen(false)}
        >
          <TemplatesSidebar
            topicsTree={topicsTree}
            userTemplates={userTemplates}
            activeTemplateId={activeTemplateId}
            mobileOpen={isMobileSidebarOpen}
            loading={isTemplatesQueryLoading}
            onClickTemplate={onActiveTemplateIdChange}
          />
        </ClickOutsideHandler>
        <div
          className={classNames(styles.contentContainer, {
            [styles.empty]: !activeTemplateId,
          })}
        >
          <TemplateMobileHeader
            openSidebar={() => setIsMobileSidebarOpen(true)}
            onClose={onClose}
          />
          {activeTemplateId ? (
            <TemplateContent
              template={template}
              loading={loading}
              owner={template?.userOwner ?? null}
              topics={template?.topicOwner ? [template.topicOwner] : []}
              updateTemplateContent={handleUpdateTemplateContent}
              updateTemplateOwner={handleUpdateTemplateOwner}
              deleteTemplate={handleDeleteTemplate}
              onCreatePost={onCreatePost}
            />
          ) : (
            <EmptyTemplate
              hasTemplates={userTemplates.length > 0 || topicsTree.length > 0}
            />
          )}
        </div>
      </div>
      <Button
        className={styles.desktopClose}
        onClick={onClose}
        to={onClose ? undefined : getPreviousRoute()}
      >
        <SVGIcon size="small">{closeIcon}</SVGIcon>
      </Button>
    </div>
  );
};

export const StatefulTemplatesModal = ({
  close,
  onCreatePost,
}: {
  close(): void;
  onCreatePost?: () => void;
}) => {
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);

  if (isFeatureEnabled('templates_v2')) {
    return <TemplatesPostModal activeTemplateId={activeTemplateId} />;
  }

  return (
    <BaseTemplatesModal
      activeTemplateId={activeTemplateId}
      onClose={close}
      onActiveTemplateIdChange={setActiveTemplateId}
      onDeleteTemplate={() => setActiveTemplateId(null)}
      onCreatePost={onCreatePost}
    />
  );
};

const RoutedTemplatesModal = () => {
  const [activeTemplateId] = useSlug('templateSlug');
  const navigate = useNavigate();

  if (isFeatureEnabled('templates_v2')) {
    return <TemplatesPostModal activeTemplateId={activeTemplateId} />;
  }

  return (
    <BaseTemplatesModal
      activeTemplateId={activeTemplateId}
      onDeleteTemplate={() => {
        navigate('/templates');
      }}
    />
  );
};

export default RoutedTemplatesModal;
